import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Ticket, Loader2, CheckCheck, Clock, HelpCircle, AlertTriangle, UserPlus, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";
import { 
  useStartChatSession, 
  useSendChatMessage, 
  useChatMessages,
  useChatMessagesSubscription,
  useConvertChatToTicket
} from "@/hooks/useSupportChat";
import { format } from "date-fns";
import { useLocale } from "@/hooks/useLocale";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface SupportChatWindowProps {
  session: { id: string; status: string; } | null | undefined;
  onSessionCreated?: () => void;
  onEscalateToTicket: () => void;
}

const MAX_CHARS = 500;

const QUICK_REPLIES = [
  { text: "Comment acheter un billet ?", icon: HelpCircle },
  { text: "Problème avec ma commande", icon: AlertTriangle },
  { text: "Devenir organisateur Pro", icon: UserPlus },
  { text: "Autre question", icon: MoreHorizontal },
];

export const SupportChatWindow = ({ session, onSessionCreated, onEscalateToTicket }: SupportChatWindowProps) => {
  const locale = useLocale();
  const [message, setMessage] = useState("");
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const startSession = useStartChatSession();
  const sendMessage = useSendChatMessage();
  const convertToTicket = useConvertChatToTicket();
  const { data: messages = [], isLoading } = useChatMessages(session?.id);
  
  useChatMessagesSubscription(session?.id);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) setShowQuickReplies(false);
  }, [messages]);

  const handleSend = async (text?: string) => {
    const content = text || message;
    if (!content.trim()) return;
    try {
      if (!session) {
        const newSession = await startSession.mutateAsync(content.trim());
        if (newSession) {
          onSessionCreated?.();
          toast.success("Conversation démarrée ! Un agent va vous répondre.");
        }
      } else {
        await sendMessage.mutateAsync({ sessionId: session.id, content: content.trim() });
      }
      setMessage("");
      setShowQuickReplies(false);
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEscalate = async () => {
    if (!session) return;
    try {
      const ticket = await convertToTicket.mutateAsync({ sessionId: session.id, subject: "Demande depuis le chat", category: "help" });
      toast.success("Ticket créé avec succès");
      navigate(`/support?ticket=${ticket.id}`);
    } catch (error) {
      toast.error("Erreur lors de la création du ticket");
    }
  };

  return (
    <div className="flex flex-col h-[450px]">
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col h-full">
            <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", damping: 15 }}
                className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-coral/20 flex items-center justify-center mb-4"
              >
                <Send className="h-8 w-8 text-primary" />
              </motion.div>
              <h4 className="font-semibold text-lg mb-2">Bienvenue ! 👋</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Notre équipe est là pour vous aider. Envoyez un message pour démarrer.
              </p>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground/70 mb-6">
                <Clock className="h-3 w-3" />
                <span>Temps de réponse moyen : ~15 min</span>
              </div>
              <AnimatePresence>
                {showQuickReplies && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="w-full space-y-2"
                  >
                    <p className="text-xs text-muted-foreground mb-2">Questions fréquentes :</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {QUICK_REPLIES.map((reply, i) => (
                        <motion.button
                          key={reply.text}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.1 }}
                          onClick={() => handleSend(reply.text)}
                          className="px-3 py-2 text-xs rounded-full border border-border bg-secondary/50 hover:bg-secondary transition-colors flex items-center gap-1.5"
                        >
                          <reply.icon className="h-3 w-3 text-muted-foreground" />
                          {reply.text}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg, index) => {
              const isUser = msg.sender_id === user?.id;
              const isLastUserMessage = isUser && index === messages.length - 1;

              return (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: "spring", damping: 20 }}
                  className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                >
                  <div className="max-w-[85%]">
                    {!isUser && (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-coral flex items-center justify-center">
                          <span className="text-[10px] text-white font-bold">S</span>
                        </div>
                        <span className="text-xs text-muted-foreground">Support</span>
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-2.5 ${
                        isUser
                          ? "bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-br-md"
                          : "bg-secondary/80 rounded-bl-md"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    </div>
                    <div className={`flex items-center gap-1 mt-1 ${isUser ? "justify-end" : "justify-start"}`}>
                      <span className="text-[10px] text-muted-foreground">
                        {format(new Date(msg.created_at), "HH:mm", { locale })}
                      </span>
                      {isUser && (
                        <CheckCheck className={`h-3 w-3 ${isLastUserMessage && session?.status === "active" ? "text-primary" : "text-muted-foreground"}`} />
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {session?.status === "waiting" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-coral flex items-center justify-center">
                  <span className="text-[10px] text-white font-bold">S</span>
                </div>
                <div className="bg-secondary/80 rounded-2xl rounded-bl-md px-4 py-2.5">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">En attente d'un agent...</span>
              </motion.div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Escalate Action Bar */}
      {session && (
        <div className="px-4 pb-2 flex items-center justify-between border-t border-border/50 pt-2 bg-muted/20">
          <Button
            variant="outline"
            size="sm"
            className="text-xs gap-1.5 border-primary/20 hover:bg-primary/5"
            onClick={handleEscalate}
            disabled={convertToTicket.isPending}
          >
            <Ticket className="h-3.5 w-3.5" />
            Créer un ticket formel
          </Button>
          {session.status === "active" && (
            <span className="text-[10px] text-green-500 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Agent connecté
            </span>
          )}
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-border bg-secondary/20">
        <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex items-end gap-2">
          <div className="flex-1 relative">
            <Textarea
              ref={inputRef}
              placeholder="Tapez votre message..."
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
              onKeyDown={handleKeyDown}
              className="min-h-[40px] max-h-[120px] resize-none bg-background"
              rows={1}
              disabled={sendMessage.isPending || startSession.isPending}
            />
          </div>
          <Button 
            type="submit" 
            size="icon"
            disabled={!message.trim() || sendMessage.isPending || startSession.isPending}
            className="bg-gradient-to-br from-primary to-coral hover:from-primary/90 hover:to-coral/90"
          >
            {sendMessage.isPending || startSession.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>

        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[9px] text-muted-foreground/50">{message.length}/{MAX_CHARS} · Shift+Entrée pour retour à la ligne</span>
          <p className="text-[9px] text-muted-foreground/50">
            Powered by E-tickets
          </p>
        </div>
      </div>
    </div>
  );
};
